import type { TranslatableCue, TranslationEngine } from './types.ts';

/** Wait after `seeked` before issuing translation fetches (scrubbing debounce). */
export const TRANSLATION_SEEK_SETTLE_MS = 500;

/** Mirrors translation.js startTranslationController selection slice (B-TR-01). */
export function cuesInTranslationWindow(
  subs: TranslatableCue[],
  currentTimeMs: number,
  windowSeconds: number,
): TranslatableCue[] {
  return subs.filter(
    (cue) => !cue.translated && Math.abs(cue.start - currentTimeMs) <= windowSeconds * 1000,
  );
}

export type TranslationPendingFlag = 'translated' | 'restored';

export type SelectTranslationCuesOptions = {
  /** Playing → prefetch span; paused → only the active cue at currentTime. */
  prefetch: boolean;
  /** Which “done” flag to respect (live translate vs restorer). */
  pendingFlag: TranslationPendingFlag;
  /**
   * When prefetching, expand the span to 2× after a non-empty 1× hit
   * (legacy startTranslationController behavior).
   */
  expandOnHit?: boolean;
};

function isPending(cue: TranslatableCue, flag: TranslationPendingFlag): boolean {
  if (flag === 'restored') return cue.restored !== true;
  return cue.translated !== true;
}

/**
 * Choose which cues to send in one translation/restorer request.
 * Prefetch (playing): legacy ±windowSeconds, optionally expand to 2×.
 * Active-only (paused): cues covering currentTime only.
 */
export function selectCuesForTranslationRequest(
  subs: TranslatableCue[],
  currentTimeMs: number,
  windowSeconds: number,
  options: SelectTranslationCuesOptions,
): TranslatableCue[] {
  const { prefetch, pendingFlag, expandOnHit = true } = options;

  if (!prefetch) {
    return subs.filter((cue) => {
      if (!isPending(cue, pendingFlag)) return false;
      const end = cue.end ?? cue.start;
      return cue.start <= currentTimeMs && currentTimeMs <= end;
    });
  }

  const near = subs.filter(
    (cue) => isPending(cue, pendingFlag)
      && Math.abs(cue.start - currentTimeMs) <= windowSeconds * 1000,
  );
  if (near.length === 0 || !expandOnHit) return near;

  return subs.filter(
    (cue) => isPending(cue, pendingFlag)
      && Math.abs(cue.start - currentTimeMs) <= windowSeconds * 2 * 1000,
  );
}

/** True while the user is scrubbing or within the post-seek settle period. */
export function shouldDeferTranslationFetch(input: {
  seeking: boolean;
  settleUntilMs: number;
  nowMs: number;
}): boolean {
  if (input.seeking) return true;
  return input.nowMs < input.settleUntilMs;
}

/** Mirrors translation.js member-only gate (B-TR-03). */
export function applyMemberOnlyGate(
  currentTrack: TranslatableCue[],
  unlock: boolean,
  service: TranslationEngine,
  memberOnlyText: string,
): boolean {
  if (!unlock && service !== 'google') {
    for (let i = 0; i < currentTrack.length; i += 1) {
      const cue = currentTrack[i];
      if (!cue) continue;
      cue.translated = true;
      cue.translation = memberOnlyText;
    }
    return true;
  }
  return false;
}

/** Mirrors message.js validate handler unlock semantics (B-MEM-02). */
export function applyMembershipUnlockFromStatus(
  storage: Record<string, unknown>,
  statusJson: string,
): Record<string, unknown> {
  const parsed = JSON.parse(statusJson) as { response?: string; ok?: boolean };
  // Only unlock on successful membership validation (OK / ok:true).
  // Failed / unauthorized / session_expired must not grant unlock.
  if (parsed.ok === true || parsed.response === 'OK') {
    return { ...storage, unlock: true };
  }
  if (
    parsed.response === 'Not Found'
    || parsed.response === 'Session Expired'
    || parsed.ok === false
  ) {
    return { ...storage, unlock: false };
  }
  return storage;
}
